'use client';

import ArticlePagination from '@/components/articlePagination/ArticlePagination';
import HeadlineField from '@/components/customFields/HeadlineField';
import { type GenericBlok, HeadlineTag } from '@/components/customFields/types';
import MagazineTrendCards from '@/components/magazineTrendCards/MagazineTrendCards';
import Pill from '@/components/pill/Pill';
import PillBar from '@/components/pillBar/PillBar';
import type { PillCardProps } from '@/components/pillCard/PillCard';
import { RichText } from '@/components/richText/RichText';
import Stage from '@/components/stage/Stage';
import { hasContent } from '@/helper/RichText';
import { linkToString } from '@/helper/urls';
import { useMagazineTrendCardsProvider } from '@/providers/MagazineTrendCardsProvider/MagazineTrendCardsProvider';
import type { PageMagCategoryStoryblok, PageMagHomeStoryblok } from '@/types/component-types-sb';
import { type ISbStoryData, type SbBlokData, storyblokEditable } from '@storyblok/react/rsc';
import { useTranslation } from 'react-i18next';

export const ITEMS_PER_PAGE = 9;

export type PageMagHomeProps = {
  articles: PillCardProps[];
  page: number;
  pages: number;
  breadcrumbs: ISbStoryData<GenericBlok>[];
  slug: string;
  story: ISbStoryData<PageMagHomeStoryblok> | undefined;
  blok: PageMagHomeStoryblok | undefined;
};

export const PageMagHome: React.FC<PageMagHomeProps> = (props) => {
  const { t } = useTranslation();
  const trendArticles = useMagazineTrendCardsProvider();

  let title = props.blok?.headline;
  if (props.page > 1) {
    title += ` / ${t('pagination.page')} ${props.page}`;
  }

  const theme = props.page === 1 ? 'dark' : '';
  return (
    <div data-testid="pagmaghome" {...storyblokEditable(props.blok as SbBlokData)}>
      <Stage
        prefix={props.blok?.sub_title}
        content={props.page === 1 ? props.blok?.description : ''}
        title={title}
        media={props.blok?.image}
        type={props.page > 1 ? 'mini' : 'default'}
      >
        {props.blok?.categories &&
          Array.isArray(props.blok.categories) &&
          props.blok.categories.length > 0 &&
          typeof props.blok.categories[0] == 'object' && (
            <PillBar>
              {(props.blok?.categories as ISbStoryData<PageMagCategoryStoryblok>[])?.map((category) => (
                <Pill
                  href={linkToString(category.full_slug)}
                  color={category.content?.color?.name || 'grey50'}
                  label={category.name}
                  key={category.uuid}
                />
              ))}
            </PillBar>
          )}
      </Stage>
      <div className="grid-wrapper">
        <div className="content-bloks-wrapper">
          {(props.blok?.introduction_title || hasContent(props.blok?.introduction)) && (
            <section>
              <HeadlineField tag={HeadlineTag.H2}>{props.blok?.introduction_title}</HeadlineField>
              <RichText text={props.blok?.introduction} />
            </section>
          )}
          {props.page === 1 && trendArticles && trendArticles.length > 0 && (
            <section>
              <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
                {t('magazine.trendTopics')}
              </HeadlineField>
              <MagazineTrendCards trendArticles={trendArticles} />
            </section>
          )}
          {props.articles && props.articles.length > 0 && (
            <section>
              <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
                {t('magazine.lastPosts')}
              </HeadlineField>
              <ArticlePagination baseUrl="/magazin/" articles={props.articles} count={props.pages} page={props.page} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
export default PageMagHome;
